<script setup lang="ts">
import type { BrandOption, ItemEditState, RevalidationFilterState, RevalidationsSortOption, RevalidationSubmitModalContinuePayload, RevalidationSubmitModalTableRow } from './revalidationsShared.types'
import type { RevalidationItem } from '~/store-v2/revalidations/revalidationList.types'
import type { RevalidationBrandSummary } from '~/store-v2/revalidations/revalidationSummary.types'
import { SolButton, SolEmptyState, useToast } from '@solfacil/girassol'
import { useRevalidationEditStates } from '~/composables/revalidations/useRevalidationEditStates'
import { useRevalidationSelection } from '~/composables/revalidations/useRevalidationSelection'
import { getPortalNameByBrand, getPortalUrlByBrand, PORTALS } from '~/pages/installation/_partials/new-experience/schemas/portals'
import { RevalidationStatus } from '~/services-v2/financial-bff/__generated__/zeus'
import { revalidationListItemKey } from '~/store-v2/revalidations/revalidationListItemKey'
import useRevalidationList from '~/store-v2/revalidations/useRevalidationList'
import useRevalidationSummary from '~/store-v2/revalidations/useRevalidationSummary'
import { firstOfSet } from '~/utils/objects'
import RevalidationSubmitModal from './revalidation-submit-modal/RevalidationSubmitModal.vue'
import RevalidationItemSkeleton from './RevalidationItemSkeleton.vue'
import {
  BATCH_BLOCKED_STATUSES,
  BATCH_SELECTION_UNAVAILABLE_TOOLTIP,
  BRAND_CHIP_STATUSES,
  BRAND_OTHER_VALUE,
  formatBrandDisplay,
  INFINITE_SCROLL_ROOT_MARGIN,
  normalizeBrandKey,
  SORT_OPTIONS,
  STATUS_LABELS,
} from './revalidations.constants'
import RevalidationsBatchBottomBar from './RevalidationsBatchBottomBar.vue'
import RevalidationsFilters from './RevalidationsFilters.vue'
import RevalidationsIntroSteps from './RevalidationsIntroSteps.vue'
import RevalidationsListRow from './RevalidationsListRow.vue'

const toast = useToast()

const { data: summary, isPending: isSummaryPending, refetch: refetchSummary } = useRevalidationSummary()

const summaryStatusCounts = computed((): Record<RevalidationStatus, number> => {
  const result: Record<RevalidationStatus, number> = {
    [RevalidationStatus.PENDING_SUBMISSION]: 0,
    [RevalidationStatus.INVALID_DATA]: 0,
    [RevalidationStatus.WAITING_COMMISSIONING_RESULT]: 0,
    [RevalidationStatus.APPROVED]: 0,
  }
  for (const { status, count } of summary.value?.statuses ?? [])
    result[status] = count
  return result
})

const sortBy = ref<'project_name' | 'status'>('project_name')
const sortDir = ref<'asc' | 'desc'>('asc')

const activeSortLabel = computed(
  () => SORT_OPTIONS.find(o => o.order_by === sortBy.value && o.order_dir === sortDir.value)?.label ?? SORT_OPTIONS[0].label,
)

function applySort(option: RevalidationsSortOption) {
  sortBy.value = option.order_by
  sortDir.value = option.order_dir
}

const brandOptions: BrandOption[] = (() => {
  const options: BrandOption[] = []
  for (const key of Object.keys(PORTALS)) {
    if (key === 'DEFAULT')
      continue
    const value = normalizeBrandKey(key)
    options.push({ name: formatBrandDisplay(value), value })
  }
  options.sort((a, b) => a.name.localeCompare(b.name))
  options.push({ name: 'Outro...', value: BRAND_OTHER_VALUE })
  return options
})()

const selectedBrands = ref<Set<string>>(new Set())
const selectedStatuses = ref<Set<RevalidationStatus>>(new Set())

type SolTagType = 'positive' | 'negative' | 'warning' | 'informative' | 'neutral'

const STATUS_TAG: Record<RevalidationStatus, { text: string, type: SolTagType }> = {
  [RevalidationStatus.PENDING_SUBMISSION]: { text: STATUS_LABELS[RevalidationStatus.PENDING_SUBMISSION], type: 'warning' },
  [RevalidationStatus.INVALID_DATA]: { text: STATUS_LABELS[RevalidationStatus.INVALID_DATA], type: 'negative' },
  [RevalidationStatus.WAITING_COMMISSIONING_RESULT]: { text: STATUS_LABELS[RevalidationStatus.WAITING_COMMISSIONING_RESULT], type: 'informative' },
  [RevalidationStatus.APPROVED]: { text: STATUS_LABELS[RevalidationStatus.APPROVED], type: 'positive' },
}

interface BrandOverride { from: string, to: string, itemStatus: RevalidationItem['status'] }
const brandOverrides = ref<Map<string, BrandOverride>>(new Map())

const sortedBrands = computed((): RevalidationBrandSummary[] => {
  const base = summary.value?.brands ?? []

  function brandChipTotal(b: RevalidationBrandSummary): number {
    return BRAND_CHIP_STATUSES.reduce(
      (sum, status) => sum + (b.statuses.find(s => s.status === status)?.count ?? 0),
      0,
    )
  }

  const chipStatuses = new Set(BRAND_CHIP_STATUSES)

  const delta = new Map<string, number>()
  for (const { from, to, itemStatus } of brandOverrides.value.values()) {
    if (!chipStatuses.has(itemStatus))
      continue
    delta.set(from, (delta.get(from) ?? 0) - 1)
    delta.set(to, (delta.get(to) ?? 0) + 1)
  }

  const summaryBrandKeys = new Set(base.map(b => normalizeBrandKey(b.brand)))
  const result: RevalidationBrandSummary[] = base.map((b) => {
    const key = normalizeBrandKey(b.brand)
    const d = delta.get(key) ?? 0
    return { ...b, total: Math.max(0, brandChipTotal(b) + d) }
  })

  for (const [brandKey, d] of delta) {
    if (!summaryBrandKeys.has(brandKey) && d > 0)
      result.push({ brand: brandKey, total: d, statuses: [] })
  }

  return result
    .filter(b => b.total > 0)
    .sort((a, b) => b.total - a.total)
})

const STATUS_FALLBACK_PRIORITY = [
  RevalidationStatus.INVALID_DATA,
  RevalidationStatus.WAITING_COMMISSIONING_RESULT,
  RevalidationStatus.APPROVED,
]

const stopAutoSelect = watch(
  [sortedBrands, summaryStatusCounts],
  ([brands, statusCounts]) => {
    if (selectedBrands.value.size > 0 || selectedStatuses.value.size > 0)
      return

    if (brands.length > 0) {
      selectedBrands.value = new Set([brands[0].brand])
      nextTick(() => stopAutoSelect())
      return
    }

    const fallbackStatus = STATUS_FALLBACK_PRIORITY.find(s => (statusCounts[s] ?? 0) > 0)
    if (fallbackStatus) {
      selectedStatuses.value = new Set([fallbackStatus])
      nextTick(() => stopAutoSelect())
    }
  },
  { immediate: true },
)

const listParams = computed(() => {
  const brand = firstOfSet(selectedBrands.value)
  const explicitStatus = firstOfSet(selectedStatuses.value)

  return {
    brand,
    status: explicitStatus
      ?? (brand
        ? [...BRAND_CHIP_STATUSES]
        : RevalidationStatus.PENDING_SUBMISSION),
    order_by: sortBy.value,
    order_dir: sortDir.value,
  }
})

const {
  data: revalidationPages,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isPending: isListPending,
  isFetching: isListFetching,
  isError: isListError,
  refetch: refetchList,
} = useRevalidationList(listParams)

const flatItems = computed(() =>
  revalidationPages.value?.pages.flatMap(p => p.items) ?? [],
)

const itemCache = ref<Map<string, RevalidationItem>>(new Map())

watch(flatItems, (items) => {
  for (const item of items)
    itemCache.value.set(revalidationListItemKey(item), item)
}, { immediate: true })

function onRowBrandChanged(rowKey: string, newBrand: BrandOption) {
  const item = itemCache.value.get(rowKey)
  if (!item)
    return

  const fromBrand = normalizeBrandKey(item.reference_inverter_brand)
  const toBrand = normalizeBrandKey(newBrand.value)

  if (fromBrand === toBrand) {
    const next = new Map(brandOverrides.value)
    next.delete(rowKey)
    brandOverrides.value = next
  }
  else {
    const next = new Map(brandOverrides.value)
    next.set(rowKey, { from: fromBrand, to: toBrand, itemStatus: item.status })
    brandOverrides.value = next
  }
}

const displayItems = computed((): RevalidationItem[] => {
  const activeBrand = firstOfSet(selectedBrands.value)

  if (!activeBrand)
    return flatItems.value

  const overrides = brandOverrides.value
  const cache = itemCache.value

  const hiddenKeys = new Set<string>()
  const injected: RevalidationItem[] = []

  for (const [rowKey, ov] of overrides) {
    if (normalizeBrandKey(ov.from) === activeBrand && normalizeBrandKey(ov.to) !== activeBrand)
      hiddenKeys.add(rowKey)
    if (normalizeBrandKey(ov.to) === activeBrand && normalizeBrandKey(ov.from) !== activeBrand) {
      const cached = cache.get(rowKey)
      if (cached)
        injected.push(cached)
    }
  }

  const base = flatItems.value.filter(item => !hiddenKeys.has(revalidationListItemKey(item)))
  return [...base, ...injected]
})

const showListSkeleton = computed(
  () => isListPending.value || (isListFetching.value && flatItems.value.length === 0),
)

const itemsByKey = computed(() => {
  const map = new Map<string, RevalidationItem>()
  for (const item of displayItems.value)
    map.set(revalidationListItemKey(item), item)
  return map
})

const listRows = computed(() =>
  displayItems.value.map((item, index) => ({
    item,
    index,
    rowKey: revalidationListItemKey(item),
  })),
)

const sentinelRef = ref<HTMLElement | null>(null)

let intersectionObserver: IntersectionObserver | null = null

watch(sentinelRef, (el, _, onCleanup) => {
  intersectionObserver?.disconnect()
  intersectionObserver = null

  if (!el) return

  intersectionObserver = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && hasNextPage.value && !isFetchingNextPage.value)
        fetchNextPage()
    },
    { rootMargin: INFINITE_SCROLL_ROOT_MARGIN },
  )
  intersectionObserver.observe(el)

  onCleanup(() => intersectionObserver?.disconnect())
}, { immediate: true })

const listAggregatesMultipleBrands = computed(() => {
  const brands = new Set<string>()
  const overrides = brandOverrides.value
  for (const item of displayItems.value) {
    const rowKey = revalidationListItemKey(item)
    const ov = overrides.get(rowKey)
    brands.add(ov ? ov.to : normalizeBrandKey(item.reference_inverter_brand))
  }
  return brands.size > 1
})

const batchSelectionBlockedByStatus = computed(() =>
  [...selectedStatuses.value].some(s => BATCH_BLOCKED_STATUSES.has(s)),
)

const batchSelectionBlocked = computed(() =>
  listAggregatesMultipleBrands.value || batchSelectionBlockedByStatus.value,
)

const {
  selectedItems,
  selectionLockedBrand,
  allSelected,
  someSelected,
  toggleItem,
  toggleSelectAll,
  selectAll,
  selectNone,
  selectFilledByPredicate,
  rowCheckboxDisabledMap,
} = useRevalidationSelection({
  itemsByKey,
  batchSelectionBlocked,
  getEffectiveBrand: (rowKey, item) => {
    const ov = brandOverrides.value.get(rowKey)
    return ov ? ov.to : normalizeBrandKey(item.reference_inverter_brand)
  },
})

function toggleBrand(brand: string) {
  selectedItems.value = new Set()
  if (selectedBrands.value.has(brand)) {
    selectedBrands.value = new Set()
  }
  else {
    selectedBrands.value = new Set([brand])
    selectedStatuses.value = new Set()
  }
}

function toggleStatus(status: RevalidationStatus) {
  selectedItems.value = new Set()
  if (selectedStatuses.value.has(status)) {
    selectedStatuses.value = new Set()
  }
  else {
    selectedStatuses.value = new Set([status])
    selectedBrands.value = new Set()
  }
}

const batchBarInverterBrandLabel = computed(() => {
  const brand = selectionLockedBrand.value
  if (brand)
    return formatBrandDisplay(brand)
  return 'marca desconhecida'
})

const {
  itemEditStates,
  getItemEditState,
  patchItemEdit,
  isItemFilled,
  deviceBrandSlugForRow,
  removeEditStatesForKeys,
} = useRevalidationEditStates({
  flatItems: computed(() => {
    const keys = new Set(flatItems.value.map(revalidationListItemKey))
    const extras = displayItems.value.filter(item => !keys.has(revalidationListItemKey(item)))
    return [...flatItems.value, ...extras]
  }),
  brandOptions,
  brandOtherValue: BRAND_OTHER_VALUE,
  protectedKeys: computed(() => new Set(brandOverrides.value.keys())),
})

function onRowEditUpdate(rowKey: string, patch: Partial<ItemEditState>) {
  if (!patch.brand || patch.brand.value === BRAND_OTHER_VALUE) {
    patchItemEdit(rowKey, patch)
    return
  }

  const item = itemCache.value.get(rowKey)
  if (!item) {
    patchItemEdit(rowKey, patch)
    return
  }

  const fromBrand = normalizeBrandKey(item.reference_inverter_brand)
  const toBrand = normalizeBrandKey(patch.brand.value)

  onRowBrandChanged(rowKey, patch.brand)
  patchItemEdit(rowKey, patch)

  if (fromBrand !== toBrand) {
    selectedItems.value = new Set()
    selectedBrands.value = new Set([toBrand])
    selectedStatuses.value = new Set()
  }
}

const DEFAULT_MONITORING_PORTAL: { name: string, href: string | null } = {
  name: 'portal de monitoramento',
  href: null,
}

const portalForItemMap = computed(() => {
  const map = new Map<string, { name: string, href: string | null }>()
  for (const { rowKey } of listRows.value) {
    const state = itemEditStates.value[rowKey]
    const val = state?.brand?.value
    if (!val || val === BRAND_OTHER_VALUE) {
      map.set(rowKey, DEFAULT_MONITORING_PORTAL)
      continue
    }
    const key = normalizeBrandKey(val).replace(/-/g, '_').toUpperCase()
    const href = getPortalUrlByBrand(key)
    map.set(rowKey, {
      name: getPortalNameByBrand(key),
      href: href && href !== '#' ? href : null,
    })
  }
  return map
})

const batchRevalidateDisabled = computed(() =>
  ![...selectedItems.value].some((rowKey) => {
    const item = itemsByKey.value.get(rowKey)
    if (!item)
      return false
    const defaultPlant = (item.plant_name?.trim()) ? item.plant_name.trim() : '—'
    return isItemFilled(rowKey, defaultPlant)
  }),
)

function selectFilled() {
  selectFilledByPredicate((rowKey, defaultPlantName) =>
    isItemFilled(rowKey, defaultPlantName))
}

const isReadOnlyStatus = computed(() => {
  const active = firstOfSet(selectedStatuses.value)
  return active === RevalidationStatus.WAITING_COMMISSIONING_RESULT
    || active === RevalidationStatus.APPROVED
})

const revalidationFilterState = computed((): RevalidationFilterState => ({
  allSelected: allSelected.value,
  someSelected: someSelected.value,
  batchSelectionDisabled: batchSelectionBlocked.value,
  batchSelectionDisabledTooltip: BATCH_SELECTION_UNAVAILABLE_TOOLTIP,
  sortBy: sortBy.value,
  sortDir: sortDir.value,
  activeSortLabel: activeSortLabel.value,
  isSummaryPending: isSummaryPending.value,
  hasSummary: !!summary.value,
  sortedBrands: sortedBrands.value,
  summaryStatusCounts: summaryStatusCounts.value,
  selectedBrands: selectedBrands.value,
  selectedStatuses: selectedStatuses.value,
}))

const revalidationSubmitModalOpen = ref(false)
const revalidationSubmitModalIsBatch = ref(true)
const revalidationSubmitModalRowKey = ref<string | null>(null)

const showBatchBottomBar = computed(() =>
  selectedItems.value.size > 0 && !revalidationSubmitModalOpen.value,
)

function validateKeysForSubmitModal(keys: readonly string[]): { ok: true } | { ok: false, message: string } {
  if (keys.length === 0)
    return { ok: false, message: 'Selecione pelo menos uma revalidação.' }
  for (const key of keys) {
    const item = itemsByKey.value.get(key)
    if (!item)
      return { ok: false, message: 'Um dos itens selecionados já não está na lista atual. Atualize e tente novamente.' }
    const state = itemEditStates.value[key]
    if (!state)
      return { ok: false, message: 'Carregando o estado de edição. Aguarde um instante e tente novamente.' }
    const defaultPlant = (item.plant_name?.trim()) ? item.plant_name.trim() : '—'
    if (!isItemFilled(key, defaultPlant)) {
      const project = (item.project_name?.trim()) ? item.project_name.trim() : '—'
      return {
        ok: false,
        message:
          keys.length === 1
            ? `Complete os dados desta linha («${project}»): indique pelo menos um número de série ou altere o nome da planta em relação ao valor por defeito.`
            : `A linha «${project}» ainda não está completa: indique pelo menos um número de série ou altere o nome da planta em relação ao valor por defeito antes de continuar.`,
      }
    }
  }
  return { ok: true }
}

function openRevalidationSubmitModalBatch() {
  const check = validateKeysForSubmitModal([...selectedItems.value])
  if (!check.ok) {
    toast.createErrorToast({ title: 'Dados incompletos', description: check.message })
    return
  }
  revalidationSubmitModalIsBatch.value = true
  revalidationSubmitModalRowKey.value = null
  revalidationSubmitModalOpen.value = true
}

function openRevalidationSubmitModalSingle(rowKey: string) {
  const check = validateKeysForSubmitModal([rowKey])
  if (!check.ok) {
    toast.createErrorToast({ title: 'Dados incompletos', description: check.message })
    return
  }
  revalidationSubmitModalIsBatch.value = false
  revalidationSubmitModalRowKey.value = rowKey
  revalidationSubmitModalOpen.value = true
}

const submitModalReferenceInverterBrand = computed((): string => {
  const resolveForKey = (rowKey: string): string => {
    const state = itemEditStates.value[rowKey]
    if (state?.brand?.value && state.brand.value !== BRAND_OTHER_VALUE)
      return state.brand.value
    if (state?.customBrand?.trim())
      return state.customBrand.trim()
    return itemsByKey.value.get(rowKey)?.reference_inverter_brand ?? ''
  }

  if (revalidationSubmitModalIsBatch.value) {
    const firstKey = firstOfSet(selectedItems.value)
    if (!firstKey)
      return ''
    return resolveForKey(firstKey)
  }
  const rk = revalidationSubmitModalRowKey.value
  if (!rk)
    return ''
  return resolveForKey(rk)
})

const submitModalCredentialsBrandSlug = computed(() =>
  normalizeBrandKey(submitModalReferenceInverterBrand.value).replace(/-/g, '_'),
)

const submitModalPortalName = computed(() =>
  getPortalNameByBrand(submitModalReferenceInverterBrand.value),
)

const submitModalPortalUrl = computed(() =>
  getPortalUrlByBrand(submitModalReferenceInverterBrand.value),
)

const submitModalTableRows = computed((): RevalidationSubmitModalTableRow[] => {
  if (!revalidationSubmitModalOpen.value)
    return []

  const keys = revalidationSubmitModalIsBatch.value
    ? [...selectedItems.value]
    : revalidationSubmitModalRowKey.value
      ? [revalidationSubmitModalRowKey.value]
      : []
  const rows: RevalidationSubmitModalTableRow[] = []
  for (const key of keys) {
    const item = itemsByKey.value.get(key)
    if (!item)
      continue
    const state = itemEditStates.value[key]
    const serials = (state?.serials ?? []).map(s => s.trim()).filter(Boolean)
    const plantFromEdit = state?.plantName?.trim() ?? ''
    const defaultPlant = (item.plant_name?.trim()) ? item.plant_name.trim() : '—'
    rows.push({
      rowKey: key,
      revalidationId: item.revalidation_id?.trim() ?? item.project_name?.trim() ?? '',
      deviceBrandSlug: deviceBrandSlugForRow(key, item),
      projectName: (item.project_name?.trim()) ? item.project_name.trim() : '—',
      inverterBrandLabel: formatBrandDisplay(deviceBrandSlugForRow(key, item)),
      serials,
      plantName: plantFromEdit || defaultPlant,
    })
  }
  return rows
})

watch(revalidationSubmitModalOpen, (isOpen) => {
  if (!isOpen) {
    revalidationSubmitModalRowKey.value = null
    revalidationSubmitModalIsBatch.value = true
  }
})

function onRevalidationSubmitModalContinue(payload?: RevalidationSubmitModalContinuePayload) {
  const wasBatch = revalidationSubmitModalIsBatch.value
  const submittedKeys = wasBatch
    ? [...selectedItems.value]
    : revalidationSubmitModalRowKey.value
      ? [revalidationSubmitModalRowKey.value]
      : []

  revalidationSubmitModalOpen.value = false

  removeEditStatesForKeys(submittedKeys)

  const nextOverrides = new Map(brandOverrides.value)
  for (const k of submittedKeys)
    nextOverrides.delete(k)
  brandOverrides.value = nextOverrides

  selectedItems.value = new Set()
  selectedBrands.value = new Set()
  selectedStatuses.value = new Set([RevalidationStatus.WAITING_COMMISSIONING_RESULT])

  void Promise.all([refetchList(), refetchSummary()])

  if (payload?.referenceBrandUpdated) {
    toast.createInformativeToast({
      title: '',
      description: wasBatch
        ? 'Revalidações enviadas, mas a marca informada não faz parte da campanha e não conta para o progresso.'
        : 'Revalidação enviada, mas a marca informada não faz parte da campanha e não conta para o progresso.',
    })
    return
  }

  toast.createSuccessToast({
    title: '',
    description: wasBatch
      ? 'Revalidações enviadas com sucesso!'
      : 'Revalidação enviada com sucesso!',
  })
}

function onBatchRevalidateSelected() {
  openRevalidationSubmitModalBatch()
}

function openHelpCenter() {
  window.open('https://helpcentersolfacil.zendesk.com/hc/pt-br', '_blank', 'noopener noreferrer')
}

watch(
  displayItems,
  (items) => {
    const valid = new Set(items.map(revalidationListItemKey))
    const sel = selectedItems.value
    for (const k of sel) {
      if (!valid.has(k)) {
        const nextSel = new Set<string>()
        for (const k2 of sel) {
          if (valid.has(k2))
            nextSel.add(k2)
        }
        selectedItems.value = nextSel
        return
      }
    }
  },
  { flush: 'pre', immediate: true },
)
</script>

<template>
  <div class="revalidations-tab">
    <RevalidationsIntroSteps />

    <div id="revalidations-session-anchor" class="revalidations-session">
      <h2 class="session-title">
        Minhas revalidações
      </h2>

      <RevalidationsFilters
        :state="revalidationFilterState"
        :sort-options="SORT_OPTIONS"
        :status-labels="STATUS_LABELS"
        @toggle-select-all="toggleSelectAll"
        @select-all="selectAll"
        @select-filled="selectFilled"
        @select-none="selectNone"
        @apply-sort="applySort"
        @toggle-brand="toggleBrand"
        @toggle-status="toggleStatus"
      />

      <ul v-if="showListSkeleton" class="revalidations-list">
        <li v-for="n in 5" :key="n">
          <RevalidationItemSkeleton />
        </li>
      </ul>

      <div
        v-else-if="isListError"
        class="revalidations-list-message"
      >
        <SolEmptyState
          id="revalidation-list-error"
          title="Não foi possível carregar as revalidações"
          subtitle="Ocorreu um erro ao buscar seus dados. Tente novamente."
          variant="no-content"
        >
          <template #action>
            <SolButton id="revalidation-retry-btn" @click="refetchList">
              Tentar novamente
            </SolButton>
          </template>
        </SolEmptyState>
      </div>

      <div
        v-else-if="displayItems.length === 0"
        class="revalidations-list-message"
      >
        <SolEmptyState
          id="revalidation-list-empty"
          title="Tudo certo por aqui!"
          subtitle="Você não possui revalidações pendentes no momento. Em caso de dúvidas, acesse nossa central de ajuda."
          variant="empty-list"
        >
          <template #action>
            <SolButton
              id="revalidation-help-center-btn"
              variant="secondary"
              @click="openHelpCenter"
            >
              Central de ajuda
            </SolButton>
          </template>
        </SolEmptyState>
      </div>

      <ul v-else class="revalidations-list">
        <RevalidationsListRow
          v-for="{ item, index, rowKey } in listRows"
          :key="rowKey"
          :item="item"
          :index="index"
          :row-key="rowKey"
          :checkbox-disabled="rowCheckboxDisabledMap.get(rowKey) ?? false"
          :checkbox-tooltip="batchSelectionBlocked ? BATCH_SELECTION_UNAVAILABLE_TOOLTIP : undefined"
          :checked="selectedItems.has(rowKey)"
          :edit-state="getItemEditState(rowKey)"
          :brand-options="brandOptions"
          :brand-other-value="BRAND_OTHER_VALUE"
          :status-tag="STATUS_TAG[item.status]"
          :portal="portalForItemMap.get(rowKey) ?? DEFAULT_MONITORING_PORTAL"
          :disabled="isReadOnlyStatus"
          @toggle="toggleItem(rowKey)"
          @update-edit="(patch) => onRowEditUpdate(rowKey, patch)"
          @submit="openRevalidationSubmitModalSingle(rowKey)"
        />
      </ul>

      <div ref="sentinelRef" class="infinite-scroll-sentinel" aria-hidden="true">
        <span v-if="isFetchingNextPage" class="infinite-scroll-loading">
          Carregando mais...
        </span>
      </div>
    </div>

    <RevalidationsBatchBottomBar
      :visible="showBatchBottomBar"
      :selected-count="selectedItems.size"
      :inverter-brand-label="batchBarInverterBrandLabel"
      :revalidate-disabled="batchRevalidateDisabled"
      @clear-selection="selectNone"
      @revalidate-selected="onBatchRevalidateSelected"
    />

    <RevalidationSubmitModal
      v-model:open="revalidationSubmitModalOpen"
      :is-batch="revalidationSubmitModalIsBatch"
      :row-key="revalidationSubmitModalRowKey"
      :portal-name="submitModalPortalName"
      :portal-url="submitModalPortalUrl"
      :table-rows="submitModalTableRows"
      :credentials-check-brand="submitModalCredentialsBrandSlug"
      @continue="onRevalidationSubmitModalContinue"
      @refetch="() => { void refetchList(); void refetchSummary() }"
      @open-help="openHelpCenter"
    />
  </div>
</template>

<style scoped lang="scss">
.revalidations-tab {
  @apply flex flex-col gap-7;
  @apply w-full;

  > .revalidations-session {
    @apply flex flex-col gap-2xs;

    > .session-title {
      @apply font-highlight;
      @apply text-neutral-low-dark text-xs;
    }

    > .revalidations-list-message {
      @apply flex flex-col items-center justify-center;
      @apply w-full;
      min-height: min(50vh, 22rem);
      @apply py-10;
    }

    > .revalidations-list {
      @apply list-none m-0 p-0;
      @apply flex flex-col gap-2;
    }
  }
}

.infinite-scroll-sentinel {
  @apply flex items-center justify-center;
  @apply py-4 min-h-[1px];

  > .infinite-scroll-loading {
    @apply fonts-body-small-regular text-neutral-low-light;
    @apply animate-pulse;
  }
}
</style>
